import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { persistAllowedFeatures, filterValidStaffFeatureIds } from '../utils/staffAccess';

const EditModeContext = createContext();

export const useEditMode = () => {
  const context = useContext(EditModeContext);
  if (!context) {
    throw new Error('useEditMode must be used within an EditModeProvider');
  }
  return context;
};

const readIsAdmin = () => localStorage.getItem('isAdmin') === 'true';
const readUserRole = () => localStorage.getItem('userRole') || '';
const readIsAuthenticated = () => localStorage.getItem('isAuthenticated') === 'true';
const readAllowedFeatures = () => {
  try {
    const raw = localStorage.getItem('allowedFeatures');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

export const EditModeProvider = ({ children }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(() => readIsAdmin());
  const [userRole, setUserRole] = useState(() => readUserRole());
  const [isAuthenticated, setIsAuthenticated] = useState(() => readIsAuthenticated());
  const [allowedFeatures, setAllowedFeatures] = useState(() => readAllowedFeatures());

  const syncFromLocalStorage = useCallback(() => {
    const admin = readIsAdmin();
    setIsAdmin(admin);
    setUserRole(readUserRole());
    setIsAuthenticated(readIsAuthenticated());
    setAllowedFeatures(readAllowedFeatures());
    if (!admin) setIsEditMode(false);
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('isAdmin');
        localStorage.removeItem('userRole');
        localStorage.removeItem('allowedFeatures');
        setIsAdmin(false);
        setUserRole('');
        setIsAuthenticated(false);
        setAllowedFeatures([]);
        setIsEditMode(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const role = data.role || '';
          const features = filterValidStaffFeatureIds(data.allowedFeatures);
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('userRole', role);
          const admin = role === 'admin';
          localStorage.setItem('isAdmin', admin ? 'true' : 'false');
          persistAllowedFeatures(features);
          setIsAdmin(admin);
          setUserRole(role);
          setIsAuthenticated(true);
          setAllowedFeatures(features);
        } else {
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.removeItem('isAdmin');
          localStorage.setItem('userRole', '');
          localStorage.removeItem('allowedFeatures');
          setIsAdmin(false);
          setUserRole('');
          setIsAuthenticated(true);
          setAllowedFeatures([]);
        }
      } catch (e) {
        console.error('Error al sincronizar rol desde Firestore', e);
        syncFromLocalStorage();
      }
    });
    return () => unsub();
  }, [syncFromLocalStorage]);

  useEffect(() => {
    syncFromLocalStorage();
    const onStorage = (e) => {
      if (
        e.key === 'isAdmin' ||
        e.key === 'userRole' ||
        e.key === 'isAuthenticated' ||
        e.key === 'allowedFeatures' ||
        e.key === null
      ) {
        syncFromLocalStorage();
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', syncFromLocalStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', syncFromLocalStorage);
    };
  }, [syncFromLocalStorage]);

  const toggleEditMode = () => {
    if (isAdmin) {
      setIsEditMode((prev) => !prev);
    }
  };

  const hasFeature = useCallback(
    (featureId) => {
      if (!featureId) return false;
      if (isAdmin) return true;
      return allowedFeatures.includes(featureId);
    },
    [isAdmin, allowedFeatures]
  );

  const value = {
    isEditMode,
    isAdmin,
    userRole,
    isAuthenticated,
    allowedFeatures,
    hasFeature,
    toggleEditMode,
    setIsEditMode
  };

  return <EditModeContext.Provider value={value}>{children}</EditModeContext.Provider>;
};
